/* eslint-disable */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    InvoicePaidStatus,
    InvoiceSearchCriteria,
    IQueryResultSettings,
    OrderInvoice
} from '@msdyn365-commerce/retail-proxy';
import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import {
    getCustomer,
    GetCustomerInput,
    getInvoices,
    GetInvoicesInput,
    IQueryResultSettingsModuleConfig,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';

/**
 * Interface for module configuration
 */
export interface IGetOrderedInvoicesConfig {
    orderBy?: string;
    itemsPerPage?: number;
}

/**
 * GetOrderedInvoices Input Action
 */
export class GetOrderedInvoicesInput implements Msdyn365.IActionInput {
    public readonly orderBy: string;
    public readonly queryResultSettings?: IQueryResultSettings;
    public readonly invoicePaidStatusValues?: InvoicePaidStatus[];

    /**
     * Constructor for the GetOrderedInvoicesInput class.
     * @param orderBy - The string to order by, e.g. "InvoiceIds desc"
     * @param queryResultSettings - The query result settings
     * @param invoicePaidStatusValues - Optional array of invoice paid status values
     */
    constructor(orderBy: string, queryResultSettings?: IQueryResultSettings, invoicePaidStatusValues?: InvoicePaidStatus[]) {
        this.orderBy = orderBy;
        this.queryResultSettings = queryResultSettings;
        this.invoicePaidStatusValues = invoicePaidStatusValues;
    }

    /**
     * Gets the cache key for this action input.
     * @returns The cache key string.
     */
    public getCacheKey = () => `ORDERED_INVOICES_${this.orderBy}_${JSON.stringify(this.invoicePaidStatusValues)}`;
    /**
     * Gets the cache object type for this action input.
     * @returns The cache object type string.
     */
    public getCacheObjectType = () => 'ORDERED_INVOICES';
    /**
     * Gets the data cache type for this action input.
     * @returns The data cache type.
     */
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

/**
 * The data model for the ordered invoices action.
 */
export type IGetOrderedInvoicesData = OrderInvoice[];

/**
 * Creates the input required to make the action call.
 * @param args - The context to create the action from.
 * @returns The action input.
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    // Extract orderBy from the module config
    const config = args.config as IGetOrderedInvoicesConfig;
    const orderBy = config.orderBy || 'InvoiceIds desc'; // Default to InvoiceIds desc as per requirement
    // Get the query result settings from the module config, or use defaults
    const queryResultSettings = QueryResultSettingsProxy.fromModuleData(
        args.requestContext,
        args.config as IQueryResultSettingsModuleConfig
    ).QueryResultSettings;
    return new GetOrderedInvoicesInput(orderBy, queryResultSettings);
};

/**
 * Parse the orderBy string to get column name and sort direction
 * @param orderBy - The order by string, e.g. "InvoiceIds desc"
 * @returns Object containing column name and isDescending flag
 */
function parseOrderBy(orderBy: string): { columnName: string; isDescending: boolean } {
    const parts = orderBy.split(' ');
    const columnName = parts[0];
    const isDescending = parts.length > 1 && parts[1].toLowerCase() === 'desc';
    
    return { columnName, isDescending };
}

/**
 * Main action function to get ordered invoices.
 * @param input - The input parameters.
 * @param ctx - The action context.
 * @returns The ordered invoices data.
 */
async function action(input: GetOrderedInvoicesInput, ctx: Msdyn365.IActionContext): Promise<OrderInvoice[]> {
    // Create search criteria
    const searchCriteria: InvoiceSearchCriteria = {};

    // Get business partner and customer info
    const businessPartner = await getCurrentAsync({ callerContext: ctx });
    searchCriteria.BusinessPartnerId = businessPartner.BusinessPartnerId;

    const customer = await getCustomer(new GetCustomerInput(ctx.requestContext.apiSettings), ctx);
    searchCriteria.CustomerId = customer.AccountNumber;

    // Set invoice paid status if provided
    if (input.invoicePaidStatusValues && input.invoicePaidStatusValues.length > 0) {
        searchCriteria.InvoicePaidStatusValues = input.invoicePaidStatusValues;
    } else {
        // Default to show all invoices
        searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.None];
    }

    // Create the query result settings and setup sorting
    const queryResultSettings = { ...input.queryResultSettings } as IQueryResultSettings;
    
    // Parse the orderBy string to get column name and sort direction
    const { columnName, isDescending } = parseOrderBy(input.orderBy);
    
    // Setup sorting using the Sorting property
    queryResultSettings.Sorting = {
        Columns: [
            {
                ColumnName: columnName,
                IsDescending: isDescending
            }
        ]
    };

    // Get the invoices with the search criteria and query settings
    const invoicesInput = new GetInvoicesInput(searchCriteria, queryResultSettings);
    console.log('invoicesInput', invoicesInput);
    const invoices = await getInvoices(invoicesInput, ctx);
    return invoices;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<OrderInvoice[]>>action,
    id: 'GetOrderedInvoices',
    input: createInput
});